import React, { useState } from 'react';
import { BookmakerLogo, BOOKMAKER_DISPLAY_NAMES } from './bookmakerAssets';
import PriceSparkline from './PriceSparkline';
import PriceHistoryChart from './PriceHistoryChart';

function formatStartTime(iso) {
    if (!iso) return '';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '';
    const now = new Date();
    const timeStr = d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

    const dayMs = 24 * 60 * 60 * 1000;
    const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
    const diffDays = Math.floor((d.getTime() - todayStart) / dayMs);

    if (diffDays === 0) return `Today ${timeStr}`;
    if (diffDays === 1) return `Tomorrow ${timeStr}`;
    if (diffDays === -1) return `Yesterday ${timeStr}`;
    const dateStr = d.toLocaleDateString([], { month: 'short', day: 'numeric' });
    return `${dateStr} ${timeStr}`;
}

function formatOdds(v) {
    if (v == null) return '—';
    return Number(v).toFixed(2);
}

function formatPP(v, { showSign = true } = {}) {
    if (v == null || Number.isNaN(Number(v))) return '—';
    const n = Number(v);
    const sign = n > 0 ? '+' : '';
    return `${showSign ? sign : ''}${n.toFixed(2)}%`;
}

function formatPercent(v, { showSign = true } = {}) {
    if (v == null || Number.isNaN(Number(v))) return '—';
    const n = Number(v);
    const sign = n > 0 ? '+' : '';
    return `${showSign ? sign : ''}${n.toFixed(2)}%`;
}

function FlameTiny({ className = 'w-3 h-3' }) {
    return (
        <svg className={className} viewBox="0 0 24 24" fill="currentColor">
            <path d="M12.982 1.566a1 1 0 00-1.586-.057C8.4 4.91 6.002 8.518 6.002 13a6 6 0 1012 0c0-2.72-1.54-5.024-3.155-6.817a1 1 0 00-1.627.226 5.506 5.506 0 01-1.42 1.787A9.24 9.24 0 0012.982 1.566zM13 18a4 4 0 01-4-4 3.98 3.98 0 01.886-2.513 7.5 7.5 0 001.345.934c.85.46 1.769.579 2.769.579 1 0 1.919-.119 2.769-.579A4 4 0 0113 18z" />
        </svg>
    );
}

function formatTickTime(iso) {
    if (!iso) return '—';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '—';
    return d.toLocaleString(undefined, {
        month: 'short',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
    });
}

function TickHistoryTable({ priceChanges }) {
    if (!Array.isArray(priceChanges) || priceChanges.length === 0) {
        return (
            <div className="px-3 py-3 text-center text-[10px] font-mono text-slate-600">
                No ticks recorded.
            </div>
        );
    }

    // Render most recent first; `priceChanges` is chronological per docs.
    const reversed = [...priceChanges].reverse();
    const lastIndex = reversed.length - 1;

    return (
        <div className="max-h-[260px] overflow-auto styled-scrollbar">
            <table className="w-full text-left">
                <thead className="sticky top-0 bg-slate-950/95 backdrop-blur-sm">
                    <tr className="border-b border-slate-800/60">
                        <th className="px-3 py-1.5 text-[9px] font-mono font-semibold text-slate-600 uppercase tracking-widest">
                            #
                        </th>
                        <th className="px-3 py-1.5 text-[9px] font-mono font-semibold text-slate-600 uppercase tracking-widest">
                            Time
                        </th>
                        <th className="px-3 py-1.5 text-[9px] font-mono font-semibold text-slate-600 uppercase tracking-widest text-right">
                            Price
                        </th>
                    </tr>
                </thead>
                <tbody>
                    {reversed.map((tick, i) => {
                        const isOpen = i === lastIndex;
                        const isCurrent = i === 0;
                        return (
                            <tr
                                key={`${tick.price_id ?? i}-${tick.timestamp}`}
                                className="border-b border-slate-800/30 last:border-0 hover:bg-slate-800/20 transition-colors"
                            >
                                <td className="px-3 py-1.5 text-[10px] font-mono text-slate-700 tabular-nums">
                                    {tick.price_id != null ? `#${tick.price_id}` : `—`}
                                </td>
                                <td className="px-3 py-1.5 text-[10px] font-mono text-slate-400 tabular-nums whitespace-nowrap">
                                    <span className="inline-flex items-center gap-1.5">
                                        {formatTickTime(tick.timestamp)}
                                        {isCurrent && (
                                            <span className="text-[8px] uppercase tracking-wider text-emerald-400 px-1 py-0.5 rounded border border-emerald-500/30 bg-emerald-500/5">
                                                current
                                            </span>
                                        )}
                                        {isOpen && !isCurrent && (
                                            <span className="text-[8px] uppercase tracking-wider text-amber-400 px-1 py-0.5 rounded border border-amber-500/30 bg-amber-500/5">
                                                open
                                            </span>
                                        )}
                                    </span>
                                </td>
                                <td className={`px-3 py-1.5 text-[11px] font-mono font-semibold tabular-nums text-right ${
                                    isCurrent ? 'text-emerald-400' : 'text-slate-200'
                                }`}>
                                    {formatOdds(tick.price)}
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
}

function BookmakerMovementItem({ bk, isBest }) {
    const [ticksOpen, setTicksOpen] = useState(false);
    const positive = (bk.percentage_change || 0) > 0;
    const sparkColor = positive ? '#34d399' : '#f87171';
    const tickCount = Array.isArray(bk.price_changes) ? bk.price_changes.length : 0;
    const canExpand = tickCount > 0;

    return (
        <div>
            <button
                type="button"
                onClick={() => canExpand && setTicksOpen((v) => !v)}
                disabled={!canExpand}
                aria-expanded={ticksOpen}
                className={`w-full grid grid-cols-[1fr_auto_auto_auto] items-center gap-3 px-3 py-2 text-left transition-colors ${
                    canExpand
                        ? 'hover:bg-slate-800/40 cursor-pointer'
                        : 'cursor-default'
                } ${ticksOpen ? 'bg-slate-800/30' : ''}`}
            >
                <div className="flex items-center gap-2 min-w-0">
                    <BookmakerLogo bookmaker={bk.bookmaker} size={18} />
                    <span className="text-xs font-mono text-slate-300 truncate">
                        {BOOKMAKER_DISPLAY_NAMES[bk.bookmaker] || bk.bookmaker}
                    </span>
                    {isBest && (
                        <span className="text-[9px] font-mono text-emerald-400/70 uppercase tracking-wider px-1 py-0.5 rounded bg-emerald-500/5 border border-emerald-500/20 whitespace-nowrap">
                            best
                        </span>
                    )}
                </div>

                <div className="hidden sm:flex items-center gap-1 font-mono tabular-nums text-[11px]">
                    <span className="text-slate-500">{formatOdds(bk.open_price)}</span>
                    <svg className="w-3 h-3 text-slate-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14 5l7 7m0 0l-7 7m7-7H3" />
                    </svg>
                    <span className={`${positive ? 'text-emerald-400' : 'text-red-400'} font-semibold`}>
                        {formatOdds(bk.current_price)}
                    </span>
                </div>

                <div className="flex items-center gap-2 flex-shrink-0">
                    <div className="hidden md:block">
                        <PriceSparkline
                            priceChanges={bk.price_changes}
                            color={sparkColor}
                            width={80}
                            height={20}
                        />
                    </div>
                    <span
                        className={`text-[11px] font-mono font-semibold tabular-nums px-1.5 py-0.5 rounded whitespace-nowrap ${
                            positive ? 'text-emerald-400 bg-emerald-500/5' : 'text-red-400 bg-red-500/5'
                        }`}
                    >
                        {formatPP(bk.percentage_change)}
                    </span>
                    <span className="hidden lg:inline text-[9px] font-mono text-slate-700 whitespace-nowrap tabular-nums">
                        {tickCount}t
                    </span>
                </div>

                <svg
                    className={`w-3 h-3 transition-all ${
                        canExpand
                            ? ticksOpen
                                ? 'rotate-180 text-emerald-400'
                                : 'text-slate-600'
                            : 'text-slate-800'
                    }`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                </svg>
            </button>

            {ticksOpen && canExpand && (
                <div className="border-t border-slate-800/60 bg-slate-950/80 animate-fade-in">
                    <TickHistoryTable priceChanges={bk.price_changes} />
                </div>
            )}
        </div>
    );
}

function ExpandedDetail({ sortedHistory, best }) {
    return (
        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)] gap-5">
            {/* Per-book breakdown */}
            <div>
                <div className="text-[10px] font-mono text-slate-500 uppercase tracking-widest mb-2 flex items-center gap-2">
                    <span>Per-book movement</span>
                    <span className="h-px flex-1 bg-slate-800/60" />
                    <span className="text-slate-600">
                        {sortedHistory.length} {sortedHistory.length === 1 ? 'book' : 'books'}
                    </span>
                </div>
                <div className="rounded-lg border border-slate-800/60 bg-slate-950/60 divide-y divide-slate-800/50 overflow-hidden">
                    {sortedHistory.map((bk) => (
                        <BookmakerMovementItem
                            key={bk.bookmaker}
                            bk={bk}
                            isBest={best && best.bookmaker === bk.bookmaker}
                        />
                    ))}
                </div>
                <p className="mt-1.5 text-[9px] font-mono text-slate-700 tracking-wide">
                    Tap a bookmaker to inspect every recorded price.
                </p>
            </div>

            {/* Overlaid price history chart */}
            <div>
                <div className="text-[10px] font-mono text-slate-500 uppercase tracking-widest mb-2 flex items-center gap-2">
                    <span>Price history</span>
                    <span className="h-px flex-1 bg-slate-800/60" />
                </div>
                <div className="rounded-lg border border-slate-800/60 bg-slate-950/60 p-3">
                    <PriceHistoryChart
                        priceHistory={sortedHistory}
                        bookmakerNames={BOOKMAKER_DISPLAY_NAMES}
                    />
                </div>
            </div>
        </div>
    );
}

// Prominent amber info badge that reveals the standard slate tooltip card on hover
// or keyboard focus, flagging a market that includes derived (modelled) prices.
// CSS-only; named group so sibling badges/rows don't trigger it, and click is
// stopped so it never toggles the clickable row.
function DerivedBadge({ size = 13, align = 'center' }) {
    const alignCls = align === 'right'
        ? 'right-0'
        : align === 'left'
            ? 'left-0'
            : 'left-1/2 -translate-x-1/2';
    return (
        <span
            tabIndex={0}
            role="button"
            aria-label="Derived prices: more info"
            onClick={(e) => e.stopPropagation()}
            className="relative group/derived inline-flex items-center align-middle cursor-help text-amber-400 hover:text-amber-300 focus:text-amber-300 focus:outline-none transition-colors"
        >
            <svg width={size} height={size} viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M18 10a8 8 0 1 1-16 0 8 8 0 0 1 16 0Zm-7-4a1 1 0 1 1-2 0 1 1 0 0 1 2 0ZM9 9a.75.75 0 0 0 0 1.5h.253a.25.25 0 0 1 .244.304l-.459 2.066A1.75 1.75 0 0 0 10.747 15H11a.75.75 0 0 0 0-1.5h-.253a.25.25 0 0 1-.244-.304l.459-2.066A1.75 1.75 0 0 0 9.253 9H9Z" clipRule="evenodd" />
            </svg>
            <span
                role="tooltip"
                className={`pointer-events-none absolute bottom-full mb-1.5 ${alignCls} z-[100]
                    w-64 max-w-[calc(100vw-1rem)] px-3 py-2.5 rounded-lg
                    bg-slate-900/95 backdrop-blur border border-slate-700/60 shadow-xl shadow-black/40
                    whitespace-normal break-words normal-case tracking-normal
                    opacity-0 translate-y-1
                    group-hover/derived:opacity-100 group-hover/derived:translate-y-0
                    group-focus/derived:opacity-100 group-focus/derived:translate-y-0
                    transition-all duration-150`}
            >
                <span className="block text-[10px] font-mono font-semibold uppercase tracking-widest text-amber-400 mb-1.5">
                    Derived Prices
                </span>
                <span className="block text-[11px] font-mono font-normal text-slate-300 leading-relaxed">
                    Some bookmaker prices for this market are derived (modelled) rather than offered directly by the bookmaker.
                </span>
            </span>
        </span>
    );
}

function HotPropRow({ prop, rank, isExpanded, onToggle, onTrack, onTip, onPropTool, canTip = false, canUsePropTool = false, colCount }) {
    const isDerived = prop.isDerived && Object.keys(prop.isDerived).length > 0;
    const priceHistory = Array.isArray(prop.price_history) ? prop.price_history : [];
    const sortedHistory = [...priceHistory].sort(
        (a, b) => (b.percentage_change || 0) - (a.percentage_change || 0)
    );
    const biggestMover = sortedHistory[0];
    const best = prop.best_odds || null;

    const isTop3 = rank <= 3;
    const isTop10 = rank <= 10;

    const rankColor = isTop3
        ? 'text-amber-400'
        : isTop10
            ? 'text-amber-500/70'
            : 'text-slate-600';

    const moverPositive = biggestMover && (biggestMover.percentage_change || 0) > 0;
    const moverSparkColor = moverPositive ? '#34d399' : '#f87171';

    const edge = best?.edge_percentage;
    const edgeClass =
        edge == null
            ? 'text-slate-700'
            : edge > 0.5
                ? 'text-emerald-300 bg-emerald-500/10 border-emerald-500/20'
                : edge > 0
                    ? 'text-emerald-400/70 bg-emerald-500/5 border-emerald-500/10'
                    : 'text-slate-500 bg-slate-800/40 border-slate-700/40';

    return (
        <>
            <tr
                onClick={onToggle}
                className={`group cursor-pointer border-b border-slate-800/40 transition-colors ${
                    isExpanded ? 'bg-slate-800/30' : 'hover:bg-slate-800/20'
                } ${isTop3 ? 'relative' : ''}`}
                aria-expanded={isExpanded}
            >
                {/* Rank */}
                <td className="px-3 py-2.5 text-center whitespace-nowrap">
                    <div className="flex items-center justify-center gap-1.5">
                        <span className={`font-mono font-bold tabular-nums text-[15px] leading-none ${rankColor}`}>
                            {String(rank).padStart(2, '0')}
                        </span>
                        {isTop3 && <FlameTiny className="w-3 h-3 text-amber-400" />}
                    </div>
                </td>

                {/* Prop (player + match meta) */}
                <td className="px-3 py-2.5 max-w-[220px] md:max-w-[320px]">
                    <div className="flex flex-col min-w-0">
                        <span className="text-xs font-semibold text-slate-300 truncate">
                            {prop.player}
                        </span>
                        <span className="text-[10px] font-mono text-slate-500 uppercase tracking-wider truncate">
                            <span className="md:hidden">{prop.market} · </span>
                            {prop.match}
                            <span className="text-slate-700"> · </span>
                            <span className="text-slate-600">{formatStartTime(prop.startTime)}</span>
                        </span>
                    </div>
                </td>

                {/* Market (desktop) */}
                <td className="px-3 py-2.5 whitespace-nowrap hidden md:table-cell">
                    <span className="inline-flex items-center gap-1.5">
                        <span className="text-xs font-mono text-slate-300">{prop.market}</span>
                        {isDerived && <DerivedBadge />}
                    </span>
                </td>

                {/* Movement chip */}
                <td className="px-3 py-2.5 whitespace-nowrap text-center">
                    <div className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-emerald-500/10 border border-emerald-500/30">
                        <svg
                            className="w-2.5 h-2.5 text-emerald-400"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2.75"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            viewBox="0 0 24 24"
                        >
                            <path d="M12 19V5M5 12l7-7 7 7" />
                        </svg>
                        <span className="text-[11px] font-mono font-bold text-emerald-400 tabular-nums leading-none">
                            {formatPercent(prop.percentage_change)}
                        </span>
                    </div>
                </td>

                {/* Biggest mover (desktop only) */}
                <td className="px-3 py-2.5 whitespace-nowrap hidden lg:table-cell">
                    {biggestMover ? (
                        <div className="flex items-center gap-2">
                            <BookmakerLogo bookmaker={biggestMover.bookmaker} size={18} />
                            <div className="flex flex-col leading-tight">
                                <span className="text-[11px] font-mono text-slate-400 tabular-nums">
                                    {formatOdds(biggestMover.open_price)}
                                    <span className="text-slate-700 mx-1">→</span>
                                    <span className="text-emerald-400 font-semibold">
                                        {formatOdds(biggestMover.current_price)}
                                    </span>
                                </span>
                                <span className="text-[9px] font-mono text-slate-600 uppercase tracking-wider">
                                    {BOOKMAKER_DISPLAY_NAMES[biggestMover.bookmaker] || biggestMover.bookmaker}
                                </span>
                            </div>
                        </div>
                    ) : (
                        <span className="text-slate-700 font-mono">—</span>
                    )}
                </td>

                {/* Best available */}
                <td className="px-3 py-2.5 whitespace-nowrap">
                    {best ? (
                        <div className="flex items-center gap-2">
                            <BookmakerLogo bookmaker={best.bookmaker} size={18} />
                            <div className="flex flex-col leading-tight">
                                <span className="text-[14px] font-mono font-bold text-emerald-400 tabular-nums">
                                    {formatOdds(best.odds)}
                                </span>
                                {edge != null ? (
                                    <div
                                        className={`inline-flex items-center gap-1 mt-0.5 px-1.5 py-0.5 rounded border w-fit ${edgeClass}`}
                                    >
                                        {edge > 0 && (
                                            <svg
                                                className="w-2.5 h-2.5"
                                                fill="none"
                                                stroke="currentColor"
                                                strokeWidth="2.5"
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                viewBox="0 0 24 24"
                                            >
                                                <path d="M7 17 17 7M9 7h8v8" />
                                            </svg>
                                        )}
                                        <span className="text-[10px] font-mono font-semibold tabular-nums leading-none">
                                            {formatPercent(edge)}
                                        </span>
                                        <span className="text-[8px] font-mono uppercase tracking-widest opacity-60 leading-none">
                                            edge
                                        </span>
                                    </div>
                                ) : (
                                    <span className="text-[9px] font-mono text-slate-700 uppercase tracking-wider">
                                        {BOOKMAKER_DISPLAY_NAMES[best.bookmaker] || best.bookmaker}
                                    </span>
                                )}
                            </div>
                        </div>
                    ) : (
                        <span className="text-slate-700 font-mono">—</span>
                    )}
                </td>

                {/* Sparkline (desktop) */}
                <td className="px-3 py-2.5 hidden md:table-cell">
                    {biggestMover ? (
                        <PriceSparkline
                            priceChanges={biggestMover.price_changes}
                            color={moverSparkColor}
                            width={100}
                            height={26}
                        />
                    ) : (
                        <span className="text-slate-700 font-mono text-xs">—</span>
                    )}
                </td>

                {/* Books count (large desktop) */}
                <td className="px-3 py-2.5 text-center hidden lg:table-cell">
                    <span className="text-[11px] font-mono text-slate-500 tabular-nums">
                        {sortedHistory.length}
                    </span>
                </td>

                {/* Track button */}
                <td className="px-2 py-2.5 text-center whitespace-nowrap">
                    <div className="inline-flex items-center gap-1">
                        <button
                            type="button"
                            onClick={(e) => {
                                e.stopPropagation();
                                if (typeof onTrack === 'function') onTrack(prop);
                            }}
                            aria-label={`Track ${prop.player} ${prop.market}`}
                            className="px-2.5 py-1 text-[11px] font-medium font-mono text-emerald-400 border border-emerald-500/20 rounded hover:bg-emerald-500/10 transition-all duration-150 whitespace-nowrap"
                        >
                            + Track
                        </button>
                        {canTip && (
                            <button
                                type="button"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    if (typeof onTip === 'function') onTip(prop);
                                }}
                                aria-label={`Tip ${prop.player} ${prop.market}`}
                                title="Submit community tip"
                                className="inline-flex items-center gap-1 px-2.5 py-1 text-[11px] font-medium font-mono text-amber-300 border border-amber-500/30 rounded hover:bg-amber-500/10 hover:border-amber-500/50 transition-all duration-150 whitespace-nowrap"
                            >
                                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="1.75" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                                </svg>
                                Tip
                            </button>
                        )}
                        {canUsePropTool && (
                            <button
                                type="button"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    if (typeof onPropTool === 'function') onPropTool(prop);
                                }}
                                aria-label={`Open Prop Tool - ${prop.player}`}
                                title={`Open ${prop.player} in the Prop Tool`}
                                className="inline-flex items-center gap-1 px-2.5 py-1 text-[11px] font-medium font-mono text-sky-300 border border-sky-500/30 rounded hover:bg-sky-500/10 hover:border-sky-500/50 hover:text-sky-200 transition-all duration-150 whitespace-nowrap"
                            >
                                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="1.75" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-4.35-4.35m1.85-5.15a7 7 0 11-14 0 7 7 0 0114 0z" />
                                </svg>
                                Prop Tool
                            </button>
                        )}
                    </div>
                </td>

                {/* Expand caret */}
                <td className="px-3 py-2.5 w-8 text-center">
                    <svg
                        className={`inline-block w-3.5 h-3.5 text-slate-500 group-hover:text-slate-300 transition-all ${
                            isExpanded ? 'rotate-180 text-emerald-400' : ''
                        }`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                    >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                    </svg>
                </td>
            </tr>

            {isExpanded && (
                <tr className="bg-slate-950/60 border-b-2 border-slate-800/80 animate-fade-in">
                    <td colSpan={colCount} className="p-4 md:p-5">
                        <ExpandedDetail sortedHistory={sortedHistory} best={best} />
                    </td>
                </tr>
            )}
        </>
    );
}

export default HotPropRow;
